import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, TextField, Grid, Alert, Snackbar,
  IconButton, Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from "@mui/material";
import { Add, Edit, Delete, Refresh } from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function CategoriasMarcas() {
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [dialogCat, setDialogCat] = useState(false);
  const [dialogMarca, setDialogMarca] = useState(false);
  const [editandoCat, setEditandoCat] = useState(null);
  const [editandoMarca, setEditandoMarca] = useState(null);
  const [catNombre, setCatNombre] = useState("");
  const [catDesc, setCatDesc] = useState("");
  const [marcaNombre, setMarcaNombre] = useState("");

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    try {
      const [catRes, marRes] = await Promise.all([
        axios.get(`${API_URL}/api/catalogo/categorias`, { headers: headers() }),
        axios.get(`${API_URL}/api/catalogo/marcas`, { headers: headers() })
      ]);
      setCategorias(Array.isArray(catRes.data) ? catRes.data : []);
      setMarcas(Array.isArray(marRes.data) ? marRes.data : []);
    } catch {}
  };

  const guardarCat = async () => {
    if (!catNombre.trim()) return;
    try {
      if (editandoCat) {
        await axios.put(`${API_URL}/api/catalogo/categorias/${editandoCat.id}`, { nombre: catNombre, descripcion: catDesc }, { headers: headers() });
      } else {
        await axios.post(`${API_URL}/api/catalogo/categorias`, { nombre: catNombre, descripcion: catDesc }, { headers: headers() });
      }
      setDialogCat(false); setCatNombre(""); setCatDesc(""); setEditandoCat(null); cargar();
      showSnackbar(editandoCat ? "Categoría actualizada" : "Categoría creada", "success");
    } catch (e) { showSnackbar(e.response?.data?.error || "Error", "error"); }
  };

  const eliminarCat = async (cat) => {
    if (!window.confirm(`¿Eliminar categoría "${cat.nombre}"?`)) return;
    try { await axios.delete(`${API_URL}/api/catalogo/categorias/${cat.id}`, { headers: headers() }); cargar(); showSnackbar("Eliminada", "success"); }
    catch (e) { showSnackbar("Error", "error"); }
  };

  const guardarMarca = async () => {
    if (!marcaNombre.trim()) return;
    try {
      if (editandoMarca) {
        await axios.put(`${API_URL}/api/catalogo/marcas/${editandoMarca.id}`, { nombre: marcaNombre }, { headers: headers() });
      } else {
        await axios.post(`${API_URL}/api/catalogo/marcas`, { nombre: marcaNombre }, { headers: headers() });
      }
      setDialogMarca(false); setMarcaNombre(""); setEditandoMarca(null); cargar();
      showSnackbar(editandoMarca ? "Marca actualizada" : "Marca creada", "success");
    } catch (e) { showSnackbar(e.response?.data?.error || "Error", "error"); }
  };

  const eliminarMarca = async (m) => {
    if (!window.confirm(`¿Eliminar marca "${m.nombre}"?`)) return;
    try { await axios.delete(`${API_URL}/api/catalogo/marcas/${m.id}`, { headers: headers() }); cargar(); showSnackbar("Eliminada", "success"); }
    catch (e) { showSnackbar("Error", "error"); }
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>🏷️ Categorías y Marcas</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Administra las categorías y marcas que puedes asignar a tus productos y servicios
      </Typography>

      <Grid container spacing={3}>
        {/* CATEGORÍAS */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>Categorías</Typography>
              <Button variant="contained" size="small" startIcon={<Add />}
                onClick={() => { setEditandoCat(null); setCatNombre(""); setCatDesc(""); setDialogCat(true); }}>
                Nueva
              </Button>
            </Box>
            {categorias.length === 0 ? (
              <Alert severity="info">No hay categorías creadas</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell><strong>Nombre</strong></TableCell>
                      <TableCell><strong>Descripción</strong></TableCell>
                      <TableCell align="center"><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categorias.map(c => (
                      <TableRow key={c.id} hover>
                        <TableCell><Typography fontWeight={600}>{c.nombre}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{c.descripcion || "—"}</Typography></TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => { setEditandoCat(c); setCatNombre(c.nombre); setCatDesc(c.descripcion || ""); setDialogCat(true); }}><Edit /></IconButton></Tooltip>
                          <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => eliminarCat(c)}><Delete /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* MARCAS */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>Marcas</Typography>
              <Button variant="contained" size="small" startIcon={<Add />}
                onClick={() => { setEditandoMarca(null); setMarcaNombre(""); setDialogMarca(true); }}>
                Nueva
              </Button>
            </Box>
            {marcas.length === 0 ? (
              <Alert severity="info">No hay marcas creadas</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                      <TableCell><strong>Nombre</strong></TableCell>
                      <TableCell align="center"><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {marcas.map(m => (
                      <TableRow key={m.id} hover>
                        <TableCell><Typography fontWeight={600}>{m.nombre}</Typography></TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => { setEditandoMarca(m); setMarcaNombre(m.nombre); setDialogMarca(true); }}><Edit /></IconButton></Tooltip>
                          <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => eliminarMarca(m)}><Delete /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog Categoría */}
      <Dialog open={dialogCat} onClose={() => setDialogCat(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{editandoCat ? "Editar Categoría" : "Nueva Categoría"}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nombre" value={catNombre} onChange={(e) => setCatNombre(e.target.value.toUpperCase())}
            sx={{ mb: 2 }} inputProps={{ style: { fontSize: 16 } }} autoFocus />
          <TextField fullWidth label="Descripción (opcional)" value={catDesc} onChange={(e) => setCatDesc(e.target.value)}
            inputProps={{ style: { fontSize: 16 } }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogCat(false)}>Cancelar</Button>
          <Button onClick={guardarCat} variant="contained">{editandoCat ? "Actualizar" : "Crear"}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Marca */}
      <Dialog open={dialogMarca} onClose={() => setDialogMarca(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{editandoMarca ? "Editar Marca" : "Nueva Marca"}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField fullWidth label="Nombre" value={marcaNombre} onChange={(e) => setMarcaNombre(e.target.value.toUpperCase())}
            inputProps={{ style: { fontSize: 16 } }} autoFocus />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogMarca(false)}>Cancelar</Button>
          <Button onClick={guardarMarca} variant="contained">{editandoMarca ? "Actualizar" : "Crear"}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
